import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useGlobalConfig } from 'context/GlobalContext';
import { useIsAppFocused } from 'hooks/useIsAppFocused';
import React, { forwardRef, memo, useImperativeHandle } from 'react';
import * as ReactNative from 'react-native';
import { Text, View } from 'react-native';
import { useDispatch } from 'react-redux';
import { backendApiSlice } from 'services/backend';
import { cmsApiSlice, useGetHomeQuery } from 'services/cms';
import theme from 'theme';
import { calcSizeForTv } from 'utils/index';
import { Platform } from 'utils/platform';

import DynamicList from './List';

import { usePlayer } from 'components/Player/context';
import IsLoading from 'components/isLoading';

export type DynamicRef = {
  refresh: () => Promise<void>;
};

export type DynamicExternalProps = {
  id: string;
  onError?: () => void;
};

export type DynamicInnerProps = {
  useGetHomeData: {
    data?: CmsHome;
    refetch: () => any;
    isFetching: boolean;
    error: FetchBaseQueryError | SerializedError | undefined;
  };
};

const Dynamic = memo(
  forwardRef<DynamicRef, DynamicExternalProps & DynamicInnerProps>((props, ref) => {
    const dispatch = useDispatch();
    const { reset } = usePlayer();

    const { data, isFetching, refetch } = props.useGetHomeData;

    const handleRefresh = async () => {
      if (Platform.OS === 'ios') {
        reset({ media: undefined });
      }
      dispatch(cmsApiSlice.util.invalidateTags(['Program', 'Highlights', 'GlobalConfig']));
      dispatch(backendApiSlice.util.invalidateTags(['Watching']));
      refetch();
    };

    useImperativeHandle(ref, () => ({ refresh: handleRefresh }), []);

    if (!data || isFetching) {
      if (!Platform.isAndroidTV) return null;

      return (
        <View style={styles.loadingContainer}>
          <IsLoading />
        </View>
      );
    }

    return (
      <View style={styles.container}>
        {Platform.isTV && data.title === 'Ouça' && (
          <Text
            style={[
              styles.title,
              {
                fontSize: calcSizeForTv(48),
                lineHeight: calcSizeForTv(60),
                fontFamily: theme.fonts.inter.bold,
                marginBottom: calcSizeForTv(60),
                marginTop: calcSizeForTv(120),
                paddingLeft: calcSizeForTv(24),
              },
            ]}>
            {data.title}
          </Text>
        )}
        <DynamicList
          items={data.modules}
          initialNumToRender={Platform.isTV ? 5 : undefined}
          refreshControl={
            <ReactNative.RefreshControl
              colors={['#fff']}
              progressBackgroundColor="#19285e"
              tintColor="#95a4da"
              refreshing={isFetching}
              onRefresh={handleRefresh}
            />
          }
        />
      </View>
    );
  }),
  (prevProps, nextProps) => {
    return (
      prevProps.id === nextProps.id &&
      prevProps.useGetHomeData.data === nextProps.useGetHomeData.data &&
      prevProps.useGetHomeData.isFetching === nextProps.useGetHomeData.isFetching &&
      prevProps.useGetHomeData.error === nextProps.useGetHomeData.error
    );
  }
);

/**
 * NOTA: Sem este shield, o componente terá inumeras re-renderizações por conta do useGetHomeQuery,
 * que atualiza varias propriedades que não utilizamos. Dessa forma, somente quando os
 * campos que nos interessam mudar é que um re-render será causado
 */
const DynamicPerformaticShield = forwardRef<DynamicRef, DynamicExternalProps>((props, ref) => {
  const { homeLivePlayer } = useGlobalConfig();
  const { isFocused, notFocusedSince } = useIsAppFocused();

  const shouldRefetch = () => {
    if (!homeLivePlayer) return true;
    if (Platform.OS === 'ios') return homeLivePlayer.ActiveHomeLivePlayerIOS;
    if (Platform.OS === 'web') return homeLivePlayer.ActiveHomeLivePlayerWeb;
    if (Platform.OS === 'android') return homeLivePlayer.ActiveHomeLivePlayerAndroid;
    return false;
  };

  const pollingConfig = { pollingInterval: shouldRefetch() ? 0 : 300000 };
  const queryData = useGetHomeQuery(props.id, pollingConfig);
  const { data, refetch, isFetching, error } = queryData;

  React.useEffect(() => {
    if (shouldRefetch()) {
      return;
    }
    const currentTime = Date.now();
    const normalizedNotFocusedSince = notFocusedSince || currentTime;
    const timestampDiff = currentTime - normalizedNotFocusedSince;
    if (isFocused && timestampDiff >= 20000) {
      refetch();
    }
  }, [isFocused, homeLivePlayer]);
  return <Dynamic ref={ref} useGetHomeData={{ data, refetch, isFetching, error }} {...props} />;
});

const styles = {
  loadingContainer: {
    flex: 1,
    justifyContent: 'center' as const,
  },
  container: {
    flex: 1,
  },
  title: {
    color: 'white',
  },
};

export default DynamicPerformaticShield;
